import React, { useCallback, useRef, useState } from 'react';
import {
  ViewMode,
  downloadChartAsImage,
  generateDownloadFilename,
} from './graphDownloadUtils';

export interface ChartPanelWrapperProps {
  /** Panel title displayed in the card header */
  title: string;
  /** Chart/graph content (React node) */
  chartContent: React.ReactNode;
  /** Table/grid content (React node). If omitted, Grid View tab is hidden. */
  gridContent?: React.ReactNode;
  /** Whether the chart is still loading (disables download) */
  loading?: boolean;
}

const ChartPanelWrapper: React.FC<ChartPanelWrapperProps> = ({
  title,
  chartContent,
  gridContent,
  loading = false,
}) => {
  const [activeView, setActiveView] = useState<ViewMode>('graph');
  const [chartNode, setChartNode] = useState<HTMLDivElement | null>(null);
  const chartRef = useRef<HTMLDivElement | null>(null);

  const chartCallbackRef = useCallback((node: HTMLDivElement | null) => {
    chartRef.current = node;
    setChartNode(node);
  }, []);

  const handleDownload = () => {
    const filename = generateDownloadFilename(title);
    downloadChartAsImage(chartRef.current, filename);
  };

  const handleTabKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>,
    view: ViewMode
  ) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setActiveView(view);
    }
  };

  const isDownloadDisabled = loading || chartNode === null;

  return (
    <div className="card">
      <div className="card-body">
        <div className="chart-panel-header">
          <h6>{title}</h6>
          <div className="view-mode-tabs" role="tablist">
            <button
              role="tab"
              aria-selected={activeView === 'graph'}
              className={activeView === 'graph' ? 'active' : ''}
              onClick={() => setActiveView('graph')}
              onKeyDown={(e) => handleTabKeyDown(e, 'graph')}
            >
              Graph View
            </button>
            {gridContent !== undefined && (
              <button
                role="tab"
                aria-selected={activeView === 'grid'}
                className={activeView === 'grid' ? 'active' : ''}
                onClick={() => setActiveView('grid')}
                onKeyDown={(e) => handleTabKeyDown(e, 'grid')}
              >
                Grid View
              </button>
            )}
          </div>
          <button
            className="download-btn"
            onClick={handleDownload}
            disabled={isDownloadDisabled}
            aria-label="Download chart as image"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </button>
        </div>
        <div role="tabpanel">
          {activeView === 'graph' ? (
            <div ref={chartCallbackRef}>{chartContent}</div>
          ) : (
            gridContent
          )}
        </div>
      </div>
    </div>
  );
};

export default ChartPanelWrapper;
